import React, { useState } from 'react';
import { Modal } from './ui/Modal';
type LegalKey = 'privacy' | 'terms' | 'disclaimer' | null;
export function Footer() {
  const [openModal, setOpenModal] = useState<LegalKey>(null);
  const closeModal = () => setOpenModal(null);
  const Separator = () => <span className="text-bio-gray" data-id="element-78">{'//'}</span>;
  return <>
      <footer className="bg-[#262626] py-12 border-t border-bio-gray/20" data-id="element-79">
        <div className="container mx-auto px-6 text-center" data-id="element-80">
          <div className="font-oswald text-2xl font-bold text-white tracking-widest mb-6 opacity-50" data-id="element-81">
            SOMIBO<span className="text-bio-teal" data-id="element-82">HEALING</span>
          </div>

          <nav className="flex items-center justify-center gap-x-4 sm:gap-x-6 mb-8 font-mono text-[10px] sm:text-xs tracking-widest uppercase" data-id="element-83">
            <button onClick={() => setOpenModal('privacy')} className="text-bio-muted hover:text-bio-teal transition-colors leading-tight text-center" data-id="element-84">
              <span className="block" data-id="element-85">Privacy</span>
              <span className="block" data-id="element-86">Policy</span>
            </button>
            <Separator data-id="element-87" />
            <button onClick={() => setOpenModal('terms')} className="text-bio-muted hover:text-bio-teal transition-colors leading-tight text-center" data-id="element-88">
              <span className="block" data-id="element-89">Terms of</span>
              <span className="block" data-id="element-90">Service</span>
            </button>
            <Separator data-id="element-91" />
            <button onClick={() => setOpenModal('disclaimer')} className="text-bio-muted hover:text-bio-teal transition-colors leading-tight text-center" data-id="element-92">
              Disclaimer
            </button>
          </nav>

          <p className="text-bio-muted/50 text-xs max-w-3xl mx-auto leading-relaxed font-light" data-id="element-93">
            The information provided in the BioCorp Protocol is for educational
            purposes only and is not intended as medical advice. Consult with a
            qualified healthcare professional before beginning any new health
            regimen, especially if you have pre-existing conditions or are
            taking medication. This product does not claim to diagnose, treat,
            cure, or prevent any disease.
          </p>

          <div className="mt-8 font-mono text-[10px] text-bio-muted/30 tracking-widest" data-id="element-94">
            {`© ${new Date().getFullYear()} SOMIBO HEALING. ALL RIGHTS RESERVED. // PROTOCOL V1.0`}
          </div>
        </div>
      </footer>

      {/* Privacy Modal */}
      <Modal isOpen={openModal === 'privacy'} onClose={closeModal} title="Privacy Policy" subtitle={'Document 01 // Sovereign Data'} data-id="element-95">
        <p data-id="element-96">
          <strong data-id="element-97">SOMIBO Healing</strong> ("we," "us," "our") respects your
          biological and digital sovereignty. This Privacy Policy explains how
          we collect, use, and protect the information you provide when
          accessing the BioCorp Protocol.
        </p>

        <h3 data-id="element-98">Information We Collect</h3>
        <p data-id="element-99">
          When you purchase or access the BioCorp Protocol, we may collect your
          name, email address, and payment confirmation details. We do not
          collect or store sensitive health information.
        </p>

        <h3 data-id="element-100">How We Use Your Information</h3>
        <p data-id="element-101">
          Your information is used solely to deliver the protocol to your secure
          inbox, process your transaction, and send relevant updates regarding
          the materials you've purchased. We do not sell, rent, or share your
          personal data with third parties for marketing purposes.
        </p>

        <h3 data-id="element-102">Cookies & Tracking</h3>
        <p data-id="element-103">
          We use minimal, anonymized analytics to understand how the protocol is
          accessed and improve the experience. No personally identifiable
          information is shared with these analytics providers.
        </p>

        <h3 data-id="element-104">Data Security</h3>
        <p data-id="element-105">
          We employ industry-standard safeguards to protect the data you
          provide, including encrypted transmission and secure storage. However,
          no system is ever 100% impenetrable, and you assume reasonable risk by
          transmitting information online.
        </p>

        <h3 data-id="element-106">Your Rights</h3>
        <p data-id="element-107">
          You may request access to, correction of, or deletion of your personal
          information at any time. To exercise these rights, submit a written
          request via the channels listed in your purchase confirmation.
        </p>

        <h3 data-id="element-108">Updates to This Policy</h3>
        <p data-id="element-109">
          We may update this Privacy Policy from time to time. Continued use of
          our materials after changes are posted constitutes acceptance of the
          revised policy.
        </p>

        <p className="text-bio-muted/70 italic mt-6" data-id="element-110">
          Last updated:{' '}
          {new Date().toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric'
        })}
          .
        </p>
      </Modal>

      {/* Terms Modal */}
      <Modal isOpen={openModal === 'terms'} onClose={closeModal} title="Terms of Service" subtitle={'Document 02 // Operating Conditions'} data-id="element-111">
        <p data-id="element-112">
          These Terms of Service ("Terms") govern your use of the BioCorp
          Protocol and any materials provided by <strong data-id="element-113">SOMIBO Healing</strong>
          . By purchasing or accessing the protocol, you agree to be bound by
          these Terms.
        </p>

        <h3 data-id="element-114">License & Use</h3>
        <p data-id="element-115">
          Upon purchase, you are granted a non-transferable, non-exclusive
          license to access and use the BioCorp Protocol materials for personal,
          non-commercial purposes. You may not redistribute, resell, sublicense,
          or publicly share the materials in any form.
        </p>

        <h3 data-id="element-116">Intellectual Property</h3>
        <p data-id="element-117">
          All content within the BioCorp Protocol — including text, frameworks,
          checklists, and the Sovereign Shield methodology — is the intellectual
          property of SOMIBO Healing and is protected by applicable copyright
          laws.
        </p>

        <h3 data-id="element-118">Personal Responsibility</h3>
        <p data-id="element-119">
          The protocol is provided "as is" for educational purposes. You
          acknowledge that any actions you take based on the materials are
          undertaken at your own risk and responsibility. Consult a qualified
          healthcare professional before making changes to your health regimen.
        </p>

        <h3 data-id="element-120">Refund Policy</h3>
        <p data-id="element-121">
          Because the BioCorp Protocol is a digital product with immediate
          delivery upon purchase, all sales are final. We do not offer refunds
          once access has been delivered.
        </p>

        <h3 data-id="element-122">Limitation of Liability</h3>
        <p data-id="element-123">
          To the fullest extent permitted by law, SOMIBO Healing shall not be
          liable for any direct, indirect, incidental, consequential, or
          punitive damages arising from your use of, or inability to use, the
          protocol materials.
        </p>

        <h3 data-id="element-124">Termination</h3>
        <p data-id="element-125">
          We reserve the right to suspend or terminate access to the materials
          at any time if these Terms are violated.
        </p>

        <h3 data-id="element-126">Changes to Terms</h3>
        <p data-id="element-127">
          These Terms may be updated periodically. Continued use after updates
          constitutes acceptance of the revised Terms.
        </p>

        <p className="text-bio-muted/70 italic mt-6" data-id="element-128">
          Last updated:{' '}
          {new Date().toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric'
        })}
          .
        </p>
      </Modal>

      {/* Disclaimer Modal */}
      <Modal isOpen={openModal === 'disclaimer'} onClose={closeModal} title="Disclaimer" subtitle={'Document 03 // Civilian Briefing'} data-id="element-129">
        <p data-id="element-130">
          The information contained within the BioCorp Protocol and any related
          materials produced by <strong data-id="element-131">SOMIBO Healing</strong> is provided
          strictly for educational and informational purposes.
        </p>

        <h3 data-id="element-132">Not Medical Advice</h3>
        <p data-id="element-133">
          The content of the BioCorp Protocol is <strong data-id="element-134">not</strong> intended
          to be a substitute for professional medical advice, diagnosis, or
          treatment. Always seek the advice of a qualified physician or other
          healthcare provider with any questions you may have regarding a
          medical condition or before starting any new health regimen,
          supplement, or herbal stack.
        </p>

        <h3 data-id="element-135">No Disease Claims</h3>
        <p data-id="element-136">
          SOMIBO Healing does not claim to diagnose, treat, cure, or prevent any
          disease. Statements made within the protocol have not been evaluated
          by the Food and Drug Administration (FDA) or any equivalent regulatory
          body in your jurisdiction.
        </p>

        <h3 data-id="element-137">Individual Results May Vary</h3>
        <p data-id="element-138">
          Any testimonials, examples, or descriptions of outcomes referenced
          within the protocol are illustrative only. Individual biology,
          lifestyle, and environment will produce different results. SOMIBO
          Healing makes no guarantees about specific outcomes.
        </p>

        <h3 data-id="element-139">Personal Responsibility</h3>
        <p data-id="element-140">
          You are responsible for your own health decisions. By engaging with
          the BioCorp Protocol, you acknowledge that you assume full
          responsibility for any actions taken based on the information provided
          and release SOMIBO Healing from any and all liability arising from
          your use of the materials.
        </p>

        <h3 data-id="element-141">Pediatric & Medication Notice</h3>
        <p data-id="element-142">
          If you are pregnant, nursing, taking medication, managing a chronic
          condition, or considering this protocol for a minor, you{' '}
          <strong data-id="element-143">must</strong> consult with a qualified healthcare provider
          before proceeding.
        </p>

        <p className="text-bio-muted/70 italic mt-6" data-id="element-144">
          By accessing the BioCorp Protocol, you acknowledge that you have read,
          understood, and agree to this disclaimer.
        </p>
      </Modal>
    </>;
}